'use client';

// MED-3: error boundary do App Router. Renderiza para qualquer erro lançado em
// rotas autenticadas ou públicas (exceto se o erro vier do RootLayout — esse
// caso é coberto por `global-error.tsx`).

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface ErrorBoundaryProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function RouteError({ error, reset }: ErrorBoundaryProps) {
  const router = useRouter();

  useEffect(() => {
    // Não logamos o `error.message` em produção para não vazar dados;
    // o `digest` é o id atribuído pelo Next que ajuda a casar com server logs.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[RouteError]', error);
    }
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-destructive/10 p-4">
            <Icon name="AlertTriangleIcon" size={48} className="text-destructive" />
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-foreground mb-2">Algo deu errado</h2>
        <p className="text-foreground/70 mb-2">
          Tivemos um problema ao carregar esta página. Tente novamente ou volte para o dashboard.
        </p>
        {error.digest ? (
          <p className="text-xs text-foreground/40 mb-6 font-mono">ref: {error.digest}</p>
        ) : (
          <div className="mb-6" />
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => reset()}
            className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="RefreshCwIcon" size={16} />
            Tentar de novo
          </button>

          <button
            onClick={() => router.push('/dashboard')}
            className="inline-flex items-center justify-center gap-2 border border-border bg-background text-foreground px-6 py-3 rounded-lg font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <Icon name="HomeIcon" size={16} />
            Ir para o dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
